"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { GroupCardService } from "@/lib/services/groupCardService";
import { UserService, DefaultCardSettingsData } from "@/lib/services/userService";
import {
  CardDetail,
  ConfettiSettings,
  Music,
  UTC_TIME_ZONES,
  CONFETTI_COLORS,
} from "@/lib/interfaces/group-card";
import styles from "./createCard.module.scss";
import LoginPopup from "@/app/components/LoginPopup/LoginPopup";
import BottomSheet from "@/app/components/cards-component/MusicSheet";
import CoverSheet from "@/app/components/cards-component/CoverSheet";
import { CardsList } from "@/lib/interfaces/cards";
import { toast, Toaster } from 'sonner';
import { RippleButton } from "@/app/components/RippleButton";
import { saveDraft, loadDraft, deleteDraft } from "@/lib/utils/draftStore";
import { authHref } from "@/lib/utils/authRedirect";

// The cropper is only needed after the user picks a file — load it on demand.
const ImageCropperModal = dynamic(() => import("@/app/components/ImageCropperModal"), { ssr: false });
import {
  CalendarClock,
  CheckCircle2,
  X,
  Images,
  LogIn,
  Music2,
  PartyPopper,
  Stamp,
  Trash2,
  Undo2,
  Upload,
  Users,
} from "lucide-react";

type ToggleRowProps = {
  name: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  children?: React.ReactNode;
};

/** A settings row with an icon, text and an on/off switch (a styled, still-native checkbox). */
function ToggleRow({ name, checked, onChange, icon, title, description, children }: ToggleRowProps) {
  const inputId = `toggle-${name}`;
  return (
    // htmlFor ties the label to the checkbox explicitly; without it a click on the row would
    // activate the first button inside `children` (e.g. "Change music") instead of the switch.
    <label
      htmlFor={inputId}
      className={`flex cursor-pointer items-center justify-between gap-3 rounded-2xl border px-3.5 py-2.5 transition-colors ${checked ? "border-primary-200 bg-primary-50/60" : "border-gray-200 bg-white hover:border-gray-300"
        }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper text-ink">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight text-ink">{title}</p>
          <p className="mt-0.5 text-xs text-gray-600">{description}</p>
          {children}
        </div>
      </div>
      <span className="relative inline-flex shrink-0">
        <input type="checkbox" id={inputId} name={name} checked={checked} onChange={onChange} className="peer sr-only" />
        <span
          className={`block h-6 w-11 rounded-full transition-colors duration-200 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 ${checked ? "bg-primary-500" : "bg-gray-300"
            }`}
        />
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${checked ? "translate-x-5" : ""
            }`}
        />
      </span>
    </label>
  );
}

/** Numbered step title with a short, always-visible hint. */
function StepHeading({ step, title, hint }: { step: number; title: string; hint?: string }) {
  return (
    <div className="mb-3 flex items-center gap-2.5">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-white" aria-hidden="true">
        {step}
      </span>
      <h3 className="font-sans text-base font-semibold leading-tight text-ink">
        <span className="sr-only">Step {step}: </span>
        {title}
      </h3>
      {hint && <p className="hidden text-xs text-gray-600 sm:block">{hint}</p>}
    </div>
  );
}

function FieldLabel({ htmlFor, children, optional }: { htmlFor: string; children: React.ReactNode; optional?: boolean }) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-[13px] font-semibold text-gray-700">
      {children}
      {optional && <span className="ml-1 font-normal text-gray-600">(optional)</span>}
    </label>
  );
}

interface CreateCardClientProps {
  card: CardDetail;
  cardSlug: string;
}

type CreateFormData = {
  cardTitle: string;
  recipientName: string;
  recipientEmail: string;
  senderName: string;
  isScheduled: boolean;
  isConfetti: boolean;
  isMusic: boolean;
  scheduleDate: string;
  scheduleTime: string;
  timeZone: string;
  customLogo: string;
  musicSettings: string;
  sendEmailSummary: boolean;
};

/** Everything needed to rebuild the create form after a login/sign-up detour. */
type CreateCardDraft = {
  formData: CreateFormData;
  selectedImage: string | null;
  previousCover: string | undefined;
  confettiSettings: ConfettiSettings | null;
  musicSettings: Music | null;
  /** Cropped (not yet uploaded) custom logo */
  logoBlob: Blob | null;
};

const draftKey = (cardSlug: string) => `create-card-draft:${cardSlug}`;
const legacyDraftKey = (cardSlug: string) => `savedFormData-${cardSlug}`;

export default function CreateCardClient({ card: masterCardDetails, cardSlug }: CreateCardClientProps) {
  const router = useRouter();
  const { data: session, status } = useSession();
  const isAuthenticated = status === "authenticated" && !!session;

  // State for login popup
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  // Set after an in-page (popup) login; the actual submit waits until the session is 'authenticated'
  const [pendingSubmit, setPendingSubmit] = useState(false);

  // Draft restore state: 'checking' until IndexedDB has been read
  const [draftStatus, setDraftStatus] = useState<"checking" | "none" | "restored">("checking");
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateFormData>({
    cardTitle: "",
    recipientName: "",
    recipientEmail: "",
    senderName: "",
    isScheduled: false,
    isConfetti: false,
    isMusic: false,
    scheduleDate: "",
    scheduleTime: "12:00",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',// "Asia/Calcutta"
    customLogo: "",
    musicSettings: "none",
    sendEmailSummary: false,
  });

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  // Cover upload / preview state
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Special features state
  const [confettiSettings, setConfettiSettings] =
    useState<ConfettiSettings | null>(null);
  const [musicSettings, setMusicSettings] = useState<Music | null>(null);
  const [musicList, setMusicList] = useState<Music[]>([]);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState<boolean>(false)
  const [previousCover, setPreviousCover] = useState<string | undefined>('')

  // Image upload state
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cropper state
  const [cropperSrc, setCropperSrc] = useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);

  // Logo upload state
  const [logoBlob, setLogoBlob] = useState<Blob | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  // Logo cropper state
  const [logoCropperSrc, setLogoCropperSrc] = useState<string | null>(null);
  const [isLogoCropperOpen, setIsLogoCropperOpen] = useState(false);

  const previewImage = selectedImage || masterCardDetails.card_image;

  // Show the skeleton again whenever the cover changes
  useEffect(() => {
    setIsPreviewLoaded(false);
  }, [previewImage]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (formData.customLogo && formData.customLogo.startsWith("blob:")) {
        URL.revokeObjectURL(formData.customLogo);
      }
    };
  }, [formData.customLogo]);

  // Min date for scheduling
  const [minDate, setMinDate] = useState("");

  // Initialize min date
  useEffect(() => {
    const today = new Date();
    setMinDate(today.toISOString().split("T")[0]);
    setFormData((prev) => ({
      ...prev,
      scheduleDate: today.toISOString().split("T")[0],
    }));
  }, []);

  const hasLoadedDefaultsRef = useRef<boolean>(false);
  const skipDefaultsRef = useRef<boolean>(false);

  // Apply default card settings if returned from getDefaultCardSettings
  const applyDefaultCardSettings = (
    data: Partial<DefaultCardSettingsData> | null | undefined
  ) => {
    if (!data) return;

    // 1. Set Confetti Settings
    if (data.confetti_settings) {
      setConfettiSettings(data.confetti_settings);
      setFormData((prev) => ({ ...prev, isConfetti: true }));
    }

    // 2. Set Custom Logo
    if (data.custom_logo) {
      setFormData((prev) => ({
        ...prev,
        customLogo: data.custom_logo!,
      }));
    }
  };

  // Fetch music list
  useEffect(() => {
    const fetchMusicList = async () => {
      try {
        const result = await GroupCardService.getMusicList();

        if (result.response_status && result.response_data) {
          const list = result.response_data as Music[];
          setMusicList(list);
          if (list.length > 0) {
            setMusicSettings((prev) => prev || list[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching music list:", error);
      }
    };

    fetchMusicList();
  }, []);

  // Fetch default card settings and apply them
  useEffect(() => {
    let isMounted = true;

    const loadDefaultSettings = async () => {
      // Don't overwrite if user has a saved form draft for this card,
      // or if the user just logged in from the popup to submit what they built
      if (draftStatus === "restored" || skipDefaultsRef.current) {
        return;
      }

      // Check if user is authenticated and fetch from server
      if (isAuthenticated) {
        try {
          const res = await UserService.getDefaultCardSettings();
          if (isMounted && res && res.response_status && res.response_data) {
            hasLoadedDefaultsRef.current = true;
            applyDefaultCardSettings(res.response_data);
            return;
          }
        } catch (error) {
          console.warn("Could not fetch default card settings from API, checking cache:", error);
        }
      }

      // Fallback to local storage cache if available and not yet loaded
      if (typeof window !== "undefined" && isMounted && !hasLoadedDefaultsRef.current) {
        try {
          const cached = localStorage.getItem("loving_default_card_settings");
          if (cached) {
            const parsed = JSON.parse(cached);
            hasLoadedDefaultsRef.current = true;
            applyDefaultCardSettings(parsed);
          }
        } catch (e) {
          console.error("Failed to load default card settings from cache:", e);
        }
      }
    };

    if (status !== "loading" && draftStatus !== "checking") {
      loadDefaultSettings();
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, status, cardSlug, draftStatus]);

  // Restore a saved draft (text fields, cover, cropped logo, music, confetti, schedule)
  // after the user comes back from logging in / signing up.
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      const draft = await loadDraft<CreateCardDraft>(draftKey(cardSlug));
      if (cancelled) return;

      if (draft && draft.formData) {
        let customLogo = draft.formData.customLogo || "";
        if (draft.logoBlob) {
          customLogo = URL.createObjectURL(draft.logoBlob);
          setLogoBlob(draft.logoBlob);
        } else if (customLogo.startsWith("blob:")) {
          customLogo = ""; // stale object URL from a previous page load
        }
        setFormData({ ...draft.formData, customLogo });
        if (draft.selectedImage) setSelectedImage(draft.selectedImage);
        if (draft.previousCover) setPreviousCover(draft.previousCover);
        if (draft.confettiSettings) setConfettiSettings(draft.confettiSettings);
        if (draft.musicSettings) setMusicSettings(draft.musicSettings);
        setDraftStatus("restored");
        setShowDraftBanner(true);
        return;
      }

      // Legacy: text-only draft in localStorage from older builds
      try {
        const saved = localStorage.getItem(legacyDraftKey(cardSlug));
        if (saved) {
          const parsed = JSON.parse(saved);
          if (typeof parsed?.customLogo === "string" && parsed.customLogo.startsWith("blob:")) {
            parsed.customLogo = "";
          }
          setFormData((prev) => ({ ...prev, ...parsed }));
          setDraftStatus("restored");
          setShowDraftBanner(true);
          return;
        }
      } catch (e) {
        console.error("Error restoring saved form data:", e);
      }

      setDraftStatus("none");
    };

    restore();
    return () => {
      cancelled = true;
    };
  }, [cardSlug]);

  /** Persist the full draft so it survives the login / sign-up / Google detour. */
  const persistDraft = async () => {
    const draft: CreateCardDraft = {
      formData: {
        ...formData,
        // blob: URLs die with the page; the Blob itself is stored separately
        customLogo: formData.customLogo.startsWith("blob:") ? "" : formData.customLogo,
      },
      selectedImage,
      previousCover,
      confettiSettings,
      musicSettings,
      logoBlob,
    };
    const saved = await saveDraft(draftKey(cardSlug), draft);
    if (!saved) {
      // IndexedDB unavailable (e.g. some private modes) — keep at least the text fields
      try {
        localStorage.setItem(legacyDraftKey(cardSlug), JSON.stringify(draft.formData));
      } catch {
        // ignore
      }
    }
  };

  const clearDraft = async () => {
    try {
      localStorage.removeItem(legacyDraftKey(cardSlug));
    } catch {
      // ignore
    }
    await deleteDraft(draftKey(cardSlug));
  };

  const handleDiscardDraft = async () => {
    await clearDraft();
    setShowDraftBanner(false);
    if (formData.customLogo.startsWith("blob:")) URL.revokeObjectURL(formData.customLogo);
    setLogoBlob(null);
    setSelectedImage(null);
    setPreviousCover("");
    setFormData((prev) => ({
      ...prev,
      cardTitle: "",
      recipientName: "",
      recipientEmail: "",
      senderName: "",
      isScheduled: false,
      isConfetti: false,
      isMusic: false,
      customLogo: "",
    }));
    toast.info("Draft discarded");
  };

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(musicList.map((music) => music.category))
    ).sort();
    return ["All", ...uniqueCategories];
  }, [musicList]);

  // Initialize confetti settings if not already set
  useEffect(() => {
    setConfettiSettings((prev) => prev || {
      shapes: {
        circle: true,
        square: true,
        star: false,
        heart: false,
        emoji: false,
      },
      colors: CONFETTI_COLORS,
      style: "snow",
    });
  }, []);

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.cardTitle.trim()) {
      newErrors.cardTitle = "Card Title is required";
    }

    if (!formData.recipientName.trim()) {
      newErrors.recipientName = "Recipient Name is required";
    }

    if (
      formData.recipientEmail.trim() &&
      !/\S+@\S+\.\S+/.test(formData.recipientEmail)
    ) {
      newErrors.recipientEmail = "Please enter a valid email address";
    }

    if (!formData.senderName.trim()) {
      newErrors.senderName = "Sender Name is required";
    }

    // Scheduled fields validation
    if (formData.isScheduled) {
      if (!formData.scheduleDate) {
        newErrors.scheduleDate = "Date is required";
      } else {
        const selectedDate = new Date(formData.scheduleDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
          newErrors.scheduleDate = "Date must be a future date";
        }
      }

      if (!formData.scheduleTime) {
        newErrors.scheduleTime = "Time is required";
      }

      if (!formData.timeZone) {
        newErrors.timeZone = "Time Zone is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);

    // Validate first, so users fix mistakes before being asked to log in
    if (!validateForm()) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
      // Save the full draft, then ask the user to log in
      await persistDraft();
      setShowLoginPopup(true);
      return;
    }

    await createCard();
  };

  // After a popup login, wait for the session to actually become 'authenticated'
  // (so the axios interceptor has a token) and submit with fresh state.
  useEffect(() => {
    if (!pendingSubmit || status === "loading") return;
    if (status === "authenticated") {
      setPendingSubmit(false);
      if (validateForm()) {
        createCard();
      }
    } else {
      // Session didn't materialise — let the user try again
      setPendingSubmit(false);
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSubmit, status]);

  const createCard = async () => {
    setIsSubmitting(true);

    try {
      let customLogoUrl: string | null = null;
      if (logoBlob) {
        const logoFile = new File([logoBlob], "custom_logo.jpg", {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
        const logoFormData = new FormData();
        logoFormData.append("image_file", logoFile, logoFile.name);

        const logoResponse = await GroupCardService.uploadImage(logoFormData);
        if (logoResponse.response_status && logoResponse.response_data) {
          customLogoUrl = logoResponse.response_data.image_file;
        } else {
          toast.error("Failed to upload custom logo");
          console.error("Failed to upload logo:", logoResponse.response_message);
          setIsSubmitting(false);
          return;
        }
      } else if (formData.customLogo && !formData.customLogo.startsWith("blob:")) {
        customLogoUrl = formData.customLogo;
      }

      const payload = {
        category: masterCardDetails.category,
        card: masterCardDetails.id,
        card_title: formData.cardTitle,
        receiver_name: formData.recipientName,
        receiver_email: formData.recipientEmail,
        sender_name: formData.senderName,
        card_image: selectedImage
          ? selectedImage
          : masterCardDetails.card_image,
        is_scheduled: formData.isScheduled,
        scheduled_date: formData.isScheduled ? formData.scheduleDate : null,
        scheduled_time: formData.isScheduled ? formData.scheduleTime : null,
        user_timezone: formData.isScheduled ? formData.timeZone : null,
        confetti_settings: formData.isConfetti ? confettiSettings : null,
        envelope_settings: null,
        custom_logo: customLogoUrl,
        music_settings: formData.isMusic ? musicSettings : null,
        card_type: "free",
      };

      const result = await GroupCardService.createCard(payload);

      if (result.response_status && result.response_data) {
        await clearDraft();
        // Take the organiser straight to the card itself
        router.push(`/card/${result.response_data.id}`);
      } else {
        // Handle error
        toast.error("Failed to create card");
        console.error("Failed to create card:", result.response_message);
      }
    } catch (error) {
      toast.error("Error creating card");
      console.error("Error creating card:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle file selection for image upload
  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    // Validate file type and size
    const maxFileSize = 2 * 1024 * 1024; // 2MB
    const allowedFileTypes = ["image/jpeg", "image/png", "image/webp"];

    if (!allowedFileTypes.includes(file.type)) {
      toast.error("Only JPEG, PNG and WebP images are allowed");
      resetFileInput();
      return;
    }

    if (file.size > maxFileSize) {
      toast.error("File size must be less than 2MB");
      resetFileInput();
      return;
    }

    // Read the file as a data URL to show in the cropper
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        setCropperSrc(reader.result as string);
        setIsCropperOpen(true);
      } else {
        toast.error("Failed to read the selected file");
      }
      resetFileInput();
    };
    reader.onerror = () => {
      toast.error("Error reading file");
      resetFileInput();
    };
    reader.readAsDataURL(file);
  };

  // Handle the cropped image blob from ImageCropperModal
  const handleCropComplete = async (croppedBlob: Blob) => {
    setIsCropperOpen(false);
    setIsUploadingCover(true);

    try {
      const croppedFile = new File([croppedBlob], "cover.jpg", {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      const formData = new FormData();
      formData.append("image_file", croppedFile, croppedFile.name);

      const response = await GroupCardService.uploadImage(formData);

      if (response.response_status && response.response_data) {
        const path = response.response_data.image_file;
        setPreviousCover(masterCardDetails.card_image || "");
        setSelectedImage(path);
        toast.success("Cover image uploaded successfully");
      } else {
        toast.error("Failed to upload cropped image");
        console.error("Failed to upload image:", response.response_message);
      }
    } catch (error) {
      toast.error("Error uploading cropped image");
      console.error("Error uploading image:", error);
    } finally {
      setIsUploadingCover(false);
    }
  };

  // Reset file input
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle file selection for logo upload
  const handleLogoFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    // Validate file type and size
    const maxFileSize = 2 * 1024 * 1024; // 2MB
    const allowedFileTypes = ["image/jpeg", "image/png", "image/webp"];

    if (!allowedFileTypes.includes(file.type)) {
      toast.error("Only JPEG, PNG, and WebP images are allowed for logo");
      resetLogoFileInput();
      return;
    }

    if (file.size > maxFileSize) {
      toast.error("Logo file size must be less than 2MB");
      resetLogoFileInput();
      return;
    }

    // Read the file as a data URL to show in the cropper
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        setLogoCropperSrc(reader.result as string);
        setIsLogoCropperOpen(true);
      } else {
        toast.error("Failed to read the selected file");
      }
      resetLogoFileInput();
    };
    reader.onerror = () => {
      toast.error("Error reading logo file");
      resetLogoFileInput();
    };
    reader.readAsDataURL(file);
  };

  // Handle the cropped logo image blob from ImageCropperModal
  const handleLogoCropComplete = (croppedBlob: Blob) => {
    setIsLogoCropperOpen(false);

    // Revoke previous blob url if any
    if (formData.customLogo && formData.customLogo.startsWith("blob:")) {
      URL.revokeObjectURL(formData.customLogo);
    }

    const blobUrl = URL.createObjectURL(croppedBlob);
    setLogoBlob(croppedBlob);
    setFormData((prev) => ({
      ...prev,
      customLogo: blobUrl,
    }));
    toast.success("Custom logo cropped and saved locally");
  };

  // Handle removing logo
  const handleRemoveLogo = () => {
    if (formData.customLogo && formData.customLogo.startsWith("blob:")) {
      URL.revokeObjectURL(formData.customLogo);
    }
    setLogoBlob(null);
    setFormData((prev) => ({
      ...prev,
      customLogo: "",
    }));
    resetLogoFileInput();
    toast.info("Custom logo removed");
  };

  // Reset logo file input
  const resetLogoFileInput = () => {
    if (logoFileInputRef.current) {
      logoFileInputRef.current.value = "";
    }
  };

  // Toggle music bottom sheet
  const toggleMusicBottomSheet = () => {
    setIsBottomSheetOpen(!isBottomSheetOpen);
  };

  // Save music settings

  const handleSelectMusic = (music: Music) => {
    setMusicSettings(music);
  };

  // Where every auth detour should bring the user back to (draft is restored there)
  const resumeUrl = `/create/${cardSlug}?resume=1`;

  const showLoginPopupOnClick = async () => {
    await persistDraft();
    setShowLoginPopup(true);
  };

  const goToSignUp = async () => {
    await persistDraft();
    router.push(authHref("/auth/register", resumeUrl));
  };

  const handleSelectCover = (card: CardsList) => {
    setPreviousCover(masterCardDetails.card_image)
    if (card.card_image) {
      setSelectedImage(card.card_image);
    }
    // The mobile sheet covers the preview, so close it; on desktop the picker
    // sits on the left and the preview stays visible to compare designs live.
    if (window.innerWidth < 1024) setIsLibraryOpen(false);
  }

  const inputClass = (field: string) =>
    `input-field-custom ${errors[field] ? "input-field-custom--error" : "input-field-custom--default"}`;

  const coverButtonClass =
    "inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all hover:border-primary-200 hover:text-primary-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <>
      <Toaster richColors />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-10">
        {/* Card preview: first on mobile (people see what they picked), sticky right column on desktop */}
        <div className="lg:order-2 lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="relative overflow-hidden rounded-3xl border border-paper-rule bg-paper-linen px-6 py-6 sm:px-10 sm:py-10">

              {selectedImage && previousCover && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedImage(previousCover);
                    setPreviousCover('');
                  }}
                  className="absolute right-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm backdrop-blur transition-colors hover:text-red-600"
                  title="Revert to previous cover"
                >
                  <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Undo cover
                </button>
              )}

              <div className={`${styles.floatingCard} relative mx-auto w-full max-w-[210px] sm:max-w-[300px] lg:max-w-[340px]`}>
                {/* Placeholder sits *behind* the image, so the server-rendered image
                    paints as soon as it arrives (no wait for hydration/onLoad = fast LCP). */}
                {!isPreviewLoaded && (
                  <div className="absolute inset-0 animate-pulse rounded-2xl bg-paper-rule" aria-hidden="true" />
                )}
                {isUploadingCover && (
                  <div className="absolute inset-0 z-[2] animate-pulse rounded-2xl bg-paper-rule/90" aria-hidden="true" />
                )}
                <Image
                  key={previewImage}
                  src={previewImage}
                  width={340}
                  height={425}
                  priority
                  sizes="(min-width: 1024px) 340px, (min-width: 640px) 300px, 210px"
                  alt={`${masterCardDetails.name} card preview`}
                  onLoad={() => setIsPreviewLoaded(true)}
                  onError={() => setIsPreviewLoaded(true)}
                  className="relative z-[1] h-auto w-full rounded-2xl object-contain shadow-[0_24px_60px_-20px_rgba(6,35,63,0.35)]"
                />
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-center">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingCover}
                className={coverButtonClass}
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
                <span>{isUploadingCover ? "Uploading..." : <>Upload<span className="hidden sm:inline"> your own</span> cover</>}</span>
              </button>
              <button type="button" onClick={() => setIsLibraryOpen(true)} className={coverButtonClass}>
                <Images className="h-4 w-4" aria-hidden="true" />
<span className="sm:hidden">Change design</span><span className="hidden sm:inline">Choose another design</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelected}
              />
              <input
                ref={logoFileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp"
                onChange={handleLogoFileSelected}
              />
            </div>

            <ul className="mt-5 hidden flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-gray-600 lg:flex">
              <li className="flex items-center gap-1.5"><Users className="h-4 w-4 text-heart-600" aria-hidden="true" />Unlimited signers</li>
              <li className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-heart-600" aria-hidden="true" />Scheduled delivery</li>
              <li className="flex items-center gap-1.5"><PartyPopper className="h-4 w-4 text-heart-600" aria-hidden="true" />Confetti &amp; music</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="lg:order-1 lg:col-span-7">
          <div id="personalise" className="scroll-mt-24 rounded-3xl border border-paper-rule bg-white p-5 shadow-[0_1px_2px_rgba(6,35,63,0.05)] sm:p-7">
            <div className="mb-5">
              <h2 className="text-2xl tracking-tight text-ink">Personalise your card</h2>
              <p className="mt-1 text-sm text-gray-600">Takes about a minute. Nothing is sent until you&apos;re ready.</p>
            </div>

            {/* Restored-draft notice */}
            {showDraftBanner && (
              <div role="status" className="mb-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                <p className="flex-1 text-sm text-emerald-900">
                  <span className="font-semibold">We saved your card — continue where you left off.</span>{" "}
                  {isAuthenticated ? "You're signed in, so you can create it now." : "Your details, cover, logo, music and confetti are all still here."}{" "}
                  <button type="button" onClick={handleDiscardDraft} className="font-semibold text-emerald-800 underline underline-offset-2">
                    Start over
                  </button>
                </p>
                <button
                  type="button"
                  onClick={() => setShowDraftBanner(false)}
                  aria-label="Dismiss saved card notice"
                  className="rounded-full p-1 text-emerald-700 hover:bg-emerald-100"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            )}


            <form onSubmit={handleSubmit} noValidate className="divide-y divide-paper-rule [&>*]:py-5 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
              <section>
                <StepHeading step={1} title="Card details" />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <FieldLabel htmlFor="recipientName">Recipient name</FieldLabel>
                    <input
                      type="text"
                      id="recipientName"
                      name="recipientName"
                      value={formData.recipientName}
                      placeholder="e.g., John Doe"
                      onChange={handleInputChange}
                      className={inputClass("recipientName")}
                    />
                    {errors.recipientName && <p className="input-error-msg">{errors.recipientName}</p>}
                  </div>
                  <div>
                    <FieldLabel htmlFor="recipientEmail" optional>Recipient email</FieldLabel>
                    <input
                      type="email"
                      id="recipientEmail"
                      name="recipientEmail"
                      value={formData.recipientEmail}
                      placeholder="e.g., john.doe@example.com"
                      onChange={handleInputChange}
                      className={inputClass("recipientEmail")}
                    />
                    {errors.recipientEmail && <p className="input-error-msg">{errors.recipientEmail}</p>}
                  </div>
                  <div>
                    <FieldLabel htmlFor="cardTitle">Card title</FieldLabel>
                    <input
                      type="text"
                      id="cardTitle"
                      name="cardTitle"
                      value={formData.cardTitle}
                      placeholder="e.g., Happy Birthday, Sarah!"
                      onChange={handleInputChange}
                      className={inputClass("cardTitle")}
                    />
                    {errors.cardTitle && <p className="input-error-msg">{errors.cardTitle}</p>}
                  </div>
                  <div>
                    <FieldLabel htmlFor="senderName">From</FieldLabel>
                    <input
                      type="text"
                      id="senderName"
                      name="senderName"
                      value={formData.senderName}
                      placeholder="e.g., Jane Smith or The Design Team"
                      onChange={handleInputChange}
                      className={inputClass("senderName")}
                    />
                    {errors.senderName && <p className="input-error-msg">{errors.senderName}</p>}
                  </div>
                </div>
              </section>

              <section>
                <StepHeading step={2} title="Delivery" hint="Optional — or send it yourself later." />
                <ToggleRow
                  name="isScheduled"
                  checked={formData.isScheduled}
                  onChange={handleInputChange}
                  icon={<CalendarClock className="h-5 w-5" aria-hidden="true" />}
                  title="Schedule delivery"
                  description="Set a future date for sending this card."
                />

                {formData.isScheduled && (
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <div>
                      <FieldLabel htmlFor="scheduleDate">Date</FieldLabel>
                      <input
                        type="date"
                        id="scheduleDate"
                        name="scheduleDate"
                        min={minDate}
                        value={formData.scheduleDate}
                        onChange={handleInputChange}
                        className={inputClass("scheduleDate")}
                      />
                      {errors.scheduleDate && <p className="input-error-msg">{errors.scheduleDate}</p>}
                    </div>
                    <div>
                      <FieldLabel htmlFor="scheduleTime">Time</FieldLabel>
                      <input
                        type="time"
                        id="scheduleTime"
                        name="scheduleTime"
                        value={formData.scheduleTime}
                        onChange={handleInputChange}
                        className={inputClass("scheduleTime")}
                      />
                      {errors.scheduleTime && <p className="input-error-msg">{errors.scheduleTime}</p>}
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <FieldLabel htmlFor="timeZone">Time zone</FieldLabel>
                      <select
                        id="timeZone"
                        name="timeZone"
                        value={formData.timeZone}
                        onChange={handleInputChange}
                        className={inputClass("timeZone")}
                      >
                        {UTC_TIME_ZONES.map((timeZone) => (
                          <option key={timeZone.name} value={timeZone.name}>
                            {timeZone.name}
                          </option>
                        ))}
                      </select>
                      {errors.timeZone && <p className="input-error-msg">{errors.timeZone}</p>}
                    </div>
                  </div>
                )}
              </section>

              <section>
                <StepHeading step={3} title="Extras" hint="Optional" />
                <div className="space-y-2">
                  <ToggleRow
                    name="isConfetti"
                    checked={formData.isConfetti}
                    onChange={handleInputChange}
                    icon={<PartyPopper className="h-5 w-5" aria-hidden="true" />}
                    title="Celebration confetti"
                    description="Add confetti to this card."
                  />

                  <div>
                    <ToggleRow
                      name="isMusic"
                      checked={formData.isMusic}
                      onChange={handleInputChange}
                      icon={<Music2 className="h-5 w-5" aria-hidden="true" />}
                      title="Background music"
                      description="Play a song when the card opens."
                    >
                      {musicSettings && formData.isMusic && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            toggleMusicBottomSheet();
                          }}
                          className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium text-primary-600 ring-1 ring-primary-200 transition-colors hover:bg-primary-50"
                        >
                          <Music2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          <span className="truncate max-w-[180px] sm:max-w-[260px]">{musicSettings.name}</span>
                          <span className="text-gray-400">· Change</span>
                        </button>
                      )}
                    </ToggleRow>
                  </div>

                  {/* Custom Logo Settings */}
                  <div className="rounded-2xl border border-gray-200 bg-white px-3.5 py-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-center gap-3.5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper text-ink">
                          <Stamp className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold leading-tight text-ink">Custom logo</p>
                          <p className="mt-0.5 text-xs text-gray-600">Add your company or team logo.</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => logoFileInputRef.current?.click()}
                        className="shrink-0 rounded-full border border-gray-200 px-3.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-primary-200 hover:text-primary-600"
                      >
                        {formData.customLogo ? "Change" : "Upload"}
                      </button>
                    </div>

                    {formData.customLogo && (
                      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white p-1">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                              src={formData.customLogo}
                              alt="Custom logo preview"
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-800">Logo attached</p>
                            <p className="text-[11px] text-gray-500">
                              {formData.customLogo.startsWith("blob:")
                                ? "Will upload when you create the card"
                                : "Default logo applied"}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="rounded-full p-2 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                          title="Remove logo"
                          aria-label="Remove logo"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <div>
                {submitted && Object.keys(errors).length > 0 && (
                  <p className="mb-3 text-sm text-red-600" role="alert">Please fill in the highlighted fields.</p>
                )}
                <RippleButton
                  type="submit"
                  disabled={isSubmitting || isUploadingCover}
                  isLoading={isSubmitting}
                  className="h-12 w-full text-base sm:w-auto sm:px-10"
                >
                  {isSubmitting ? "Creating..." : "Create my card"}
                </RippleButton>
                {!isAuthenticated && status !== "loading" ? (
                  <p className="mt-3 flex items-start gap-2 text-sm text-gray-600">
                    <LogIn className="mt-0.5 h-4 w-4 shrink-0 text-heart-600" aria-hidden="true" />
                    <span>
                      Next you&apos;ll{" "}
                      <button type="button" onClick={showLoginPopupOnClick} className="font-semibold text-heart-700 underline-offset-2 hover:underline">
                        log in
                      </button>{" "}
                      or{" "}
                      <button type="button" onClick={goToSignUp} className="font-semibold text-heart-700 underline-offset-2 hover:underline">
                        create a free account
                      </button>
                      {" "}— everything you&apos;ve entered is kept.
                    </span>
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-gray-600">Next, you&apos;ll get a link to invite everyone to sign.</p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <BottomSheet
        isOpen={isBottomSheetOpen}
        onClose={() => setIsBottomSheetOpen(false)}
        title="Select Music"
        categories={categories}
        musicList={musicList}
        onSelect={handleSelectMusic}
      />

      {/* Library Bottom Sheet */}
      {masterCardDetails.category && (
        <CoverSheet
          isOpen={isLibraryOpen}
          onClose={() => { setIsLibraryOpen(false) }}
          title='Choose a design'
          categorySlug={masterCardDetails.category}
          onSelect={handleSelectCover}
          isCategoryUUID={true}
          categoryUUID={masterCardDetails.category}
        />
      )}

      {/* Login Popup */}
      <LoginPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
        onSuccess={() => {
          // Close the popup; the pendingSubmit effect creates the card once the
          // session status flips to 'authenticated' (avoids a stale isAuthenticated closure).
          setShowLoginPopup(false);
          skipDefaultsRef.current = true;
          setIsSubmitting(true);
          setPendingSubmit(true);
        }}
        onBeforeRedirect={persistDraft}
        callbackUrl={resumeUrl}
        description="Please log in to create and save cards. Your card details are saved and will be waiting for you."
      />

      {/* Image Cropper Modal */}
      {cropperSrc && (
        <ImageCropperModal
          isOpen={isCropperOpen}
          imageSrc={cropperSrc}
          aspectRatio={4 / 5}
          cropType="cover"
          onCropComplete={handleCropComplete}
          onCancel={() => setIsCropperOpen(false)}
        />
      )}

      {/* Logo Cropper Modal */}
      {logoCropperSrc && (
        <ImageCropperModal
          isOpen={isLogoCropperOpen}
          imageSrc={logoCropperSrc}
          aspectRatio={16 / 9}
          cropType="logo"
          title="Crop Custom Logo"
          onCropComplete={handleLogoCropComplete}
          onCancel={() => setIsLogoCropperOpen(false)}
        />
      )}
    </>
  );
}
